import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import * as React from "react";
import { useSearchParams } from "react-router-dom";

import {
  CHART_DISPLAY_TYPES,
  ChartDisplayType,
  MetricDefinitionRecord,
  NUMERIC_AGGREGATIONS,
  NumericAggregation,
  Period,
} from "@/shared/domain/metrics";

import { BucketWindow } from "./bucketWindow";
import {
  DASHBOARD_PARAMS,
  mergeParams,
  readChartAggregation,
  readChartDisplay,
  readChartMetric,
} from "./dashboardParams";
import { DrillDownDialog } from "./DrillDownDialog";
import {
  clearDrilldownParams,
  readDrilldownParams,
  setDrilldownParams,
} from "./drilldownParams";
import { FuzzySelect } from "./FuzzySelect";
import { MetricChart } from "./MetricChart";
import styles from "./MetricChartCard.module.css";

export interface MetricChartCardProps {
  definitions: MetricDefinitionRecord[];
  orgId: string | undefined;
  period: Period;
  startTimeNs: number;
  endTimeNs: number;
  deviceIds?: string[];
  sessionIds?: string[];
}

/**
 * Full-width chart card. Owns the per-card metric / aggregation / display
 * pickers and the drill-down dialog opened by clicking a chart bucket.
 */
export function MetricChartCard({
  definitions,
  orgId,
  period,
  startTimeNs,
  endTimeNs,
  deviceIds,
  sessionIds,
}: MetricChartCardProps) {
  const names = definitions.map((d) => d.name);
  const [searchParams, setSearchParams] = useSearchParams();

  // The entire view lives in the URL so it restores itself on reload or when
  // returning via browser back (e.g. after clicking through to a session).
  // Readers default sensibly and drop selections whose metric no longer exists.
  const selectedName = readChartMetric(searchParams, names);
  const aggregation = readChartAggregation(searchParams);
  const display = readChartDisplay(searchParams);
  const drill = readDrilldownParams(searchParams);

  const setSelectedName = (next: string) =>
    setSearchParams(
      mergeParams(searchParams, { [DASHBOARD_PARAMS.chartMetric]: next }),
      { replace: true },
    );
  const setAggregation = (next: NumericAggregation) =>
    setSearchParams(
      mergeParams(searchParams, { [DASHBOARD_PARAMS.chartAggregation]: next }),
      { replace: true },
    );
  const setDisplay = (next: ChartDisplayType) =>
    setSearchParams(
      mergeParams(searchParams, { [DASHBOARD_PARAMS.chartDisplay]: next }),
      { replace: true },
    );

  const openDrill = React.useCallback(
    (bucket: BucketWindow) => {
      setSearchParams(
        setDrilldownParams(searchParams, {
          metricName: selectedName,
          bucket,
          period,
        }),
        { replace: true },
      );
    },
    [searchParams, setSearchParams, selectedName, period],
  );

  const closeDrill = React.useCallback(() => {
    setSearchParams(clearDrilldownParams(searchParams), { replace: true });
  }, [searchParams, setSearchParams]);

  if (names.length === 0) {
    return null;
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <FuzzySelect
          className={styles.metricPicker}
          label="Metric"
          value={selectedName}
          options={names}
          onChange={setSelectedName}
        />
        <FuzzySelect
          className={styles.smallPicker}
          label="Aggregation"
          value={aggregation}
          options={[...NUMERIC_AGGREGATIONS]}
          onChange={(next) => setAggregation(next as NumericAggregation)}
        />
        <FormControl size="small" className={styles.smallPicker}>
          <InputLabel id="chart-display">Display</InputLabel>
          <Select
            labelId="chart-display"
            label="Display"
            value={display}
            onChange={(e: SelectChangeEvent) =>
              setDisplay(e.target.value as ChartDisplayType)
            }
          >
            {CHART_DISPLAY_TYPES.map((d) => (
              <MenuItem key={d.value} value={d.value}>
                {d.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <MetricChart
        metricName={selectedName}
        orgId={orgId}
        period={period}
        aggregation={aggregation}
        startTimeNs={startTimeNs}
        endTimeNs={endTimeNs}
        deviceIds={deviceIds}
        sessionIds={sessionIds}
        display={display}
        onBucketClick={openDrill}
      />

      <DrillDownDialog
        open={drill !== null}
        onClose={closeDrill}
        metricName={drill?.metricName ?? selectedName}
        period={drill?.period ?? period}
        bucket={drill?.bucket ?? null}
        orgId={orgId}
        deviceIds={deviceIds}
        sessionIds={sessionIds}
      />
    </div>
  );
}
